// feedback.ts — 개선·에러 리포트 보드 순수 로직 (타입/라벨/그룹핑/검증).
// 화면(FeedbackScreen)에는 계산 로직을 두지 않는다.

export type FeedbackType = "improvement" | "bug";
export type FeedbackStatus = "open" | "in_progress" | "done" | "on_hold";
export type FeedbackPriority = "high" | "medium" | "low";

export interface FeedbackPost {
  id: string;
  type: FeedbackType;
  title: string;
  body: string;
  status: FeedbackStatus;
  priority: FeedbackPriority;
  images: string[];
  authorUid: string;
  authorName: string;
  createdAt: string;
  updatedAt: string;
}

export interface FeedbackComment {
  id: string;
  postId: string;
  body: string;
  authorUid: string;
  authorName: string;
  createdAt: string;
}

export interface Me {
  uid: string;
  email: string;
  name: string;
  role: "admin" | "member";
  position: string;
  dept: string;
  imagesEnabled: boolean;
  isSuperadmin: boolean;
}

export const STATUS_ORDER: FeedbackStatus[] = ["open", "in_progress", "done", "on_hold"];

export const STATUS_LABELS: Record<FeedbackStatus, string> = {
  open: "접수",
  in_progress: "진행중",
  done: "완료",
  on_hold: "보류 · 반려",
};

export const TYPE_LABELS: Record<FeedbackType, string> = {
  improvement: "개선",
  bug: "에러",
};

export const PRIORITY_LABELS: Record<FeedbackPriority, string> = {
  high: "높음",
  medium: "보통",
  low: "낮음",
};

const PRIORITY_ORDER: Record<FeedbackPriority, number> = { high: 0, medium: 1, low: 2 };

export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
export const MAX_IMAGES_PER_POST = 5;

/** 칸반 컬럼 그룹핑 — 각 컬럼은 우선순위(높음 먼저) → 최신 작성순. */
export function groupByStatus(posts: FeedbackPost[]): Record<FeedbackStatus, FeedbackPost[]> {
  const grouped: Record<FeedbackStatus, FeedbackPost[]> = {
    open: [],
    in_progress: [],
    done: [],
    on_hold: [],
  };
  for (const p of posts) grouped[p.status].push(p);
  for (const status of STATUS_ORDER) {
    grouped[status].sort(
      (a, b) =>
        PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority] ||
        b.createdAt.localeCompare(a.createdAt)
    );
  }
  return grouped;
}

export interface PostForm {
  type: FeedbackType;
  title: string;
  body: string;
  priority: FeedbackPriority;
}

/** 등록/수정 폼 검증 — 통과 시 null, 실패 시 토스트 메시지. */
export function validatePostForm(form: PostForm): string | null {
  if (!form.title.trim()) return "제목을 입력해 주세요";
  if (!form.body.trim()) return "내용을 입력해 주세요";
  return null;
}

/** 첨부 파일 검증 — 통과 시 null. currentCount 는 이미 첨부된 장수. */
export function validateImageFile(
  file: { type: string; size: number },
  currentCount: number
): string | null {
  if (currentCount >= MAX_IMAGES_PER_POST)
    return `이미지는 최대 ${MAX_IMAGES_PER_POST}장까지 첨부할 수 있습니다`;
  if (!file.type.startsWith("image/")) return "이미지 파일만 첨부할 수 있습니다";
  if (file.size > MAX_IMAGE_BYTES) return "이미지는 5MB 이하만 첨부할 수 있습니다";
  return null;
}

export const canEditPost = (post: FeedbackPost, me: Me | null): boolean =>
  !!me && post.authorUid === me.uid;

export const canChangeStatus = (me: Me | null): boolean => me?.role === "admin";
